import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Slice9Panel from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Panel";
import MovieContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/MovieContent";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import { isNull } from "../../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import GameEvent from "../../../core/application/GameEvent";
import MediatorManager from "../../../core/application/MediatorManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import AssetLoadService from "../../../core/assetloading/AssetLoadService";
import Config from "../../../core/config/Config";
import SoundType from "../../../core/sound/SoundType";
import SystemMessageBuilder from "../../../core/system-message/controller/SystemMessageBuilder";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinFactory from "../../../core/ui/SkinFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import VideoPlayer from "../../../core/video/VideoPlayer";
import VideoPlayerDispenserHelper from "../../../core/video/VideoPlayerDispenserHelper";
import VideoPlayerEvent from "../../../core/video/VideoPlayerEvent";
import NGTAssetLocator from "../../base/NGTAssetLocator";
import DebugEvent from "../../debug/DebugEvent";
import NPCModel from "../../scenario/NPCModel";
import ScenarioLibrary from "../../scenario/ScenarioLibrary";
import { AbstractDialogPlayerMediator } from "./AbstractDialogPlayerMediators";
import DialogOptionHelper from "./DialogOptionHelper";

/**
 * DialogPlayerMediator.js
 * 
 * Created on 3-okt-2013, 15:26:13
 *
 * @author Ivo Swartjes for Ranj
 **/
export default class DialogPlayerMediator extends AbstractDialogPlayerMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {SoundManager} soundManager 
     * @param {AssetLibrary} assetLibrary 
     * @param {NGTAssetLocator} assetLocator 
     * @param {AssetLoadService} assetLoadService 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {MediatorManager} mediatorManager 
     * @param {NPCModel} npcModel 
     * @param {Config} config 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     */
    constructor(target, dispatcher, soundManager, assetLibrary, assetLocator, assetLoadService, scenarioLibrary,
        mediatorManager, npcModel, config, layoutManager, clickEaterFactory, languagePack) {

        super(target, dispatcher, layoutManager, clickEaterFactory);

        this.npcID = "NPC";
        this.youID = "You";

        this._skin = target;

        this._dispatcher = dispatcher;
        this._soundManager = soundManager;
        this._assetLibrary = assetLibrary;
        this._assetLocator = assetLocator;
        this._assetLoadService = assetLoadService;
        this._scenarioLibrary = scenarioLibrary;
        this._mediatorManager = mediatorManager;
        this._npcModel = npcModel;
        this._config = config;
        this._languagePack = languagePack;

        this._history;

        this._npcTextPanelLeft;
        this._npcTextPanelRight;
        this._currentNpcTextPanel;

        this._currentAudioID;
        this._currentAudioContent;
        this._contentPanel;

        this._npcContent;

        this._dialogPlayerOptionsMediator;

        this._bgImgToggle = 0;
    }

    initialize() {
        // initialize object that will contain the image content related to its ID
        this._imagesContent = {};

        // Sub-mediators
        this._dialogPlayerOptionsMediator = this._mediatorManager.createMediator(this._skin["dialogPanel"], SkinKeyRegistry.DIALOG_OPTIONS_SKIN);
        this._dialogTimerMediator = this._mediatorManager.createMediator(this._skin["dialogTimer"], SkinKeyRegistry.DIALOG_TIMER_SKIN);

        this._npcTextPanelLeft = this._skin.npcPanel;
        this._npcTextPanelLeft.defaultX = this._npcTextPanelLeft.x;
        this._npcTextPanelLeft.defaultY = this._npcTextPanelLeft.y;
        this._npcTextPanelLeft.slicedNpcTextPanel = new Slice9Panel(this._npcTextPanelLeft.npcTextPanel,
            this._npcTextPanelLeft.npcTextPanel.children.find(c => c instanceof createjs.Bitmap), { mode: Slice9Panel.VERTICAL });

        this._npcTextPanelRight = this._skin.npcPanelFlipped;
        this._npcTextPanelRight.slicedNpcTextPanel = new Slice9Panel(this._npcTextPanelRight.npcTextPanel,
            this._npcTextPanelRight.npcTextPanel.children.find(c => c instanceof createjs.Bitmap), { mode: Slice9Panel.VERTICAL });
        this._npcTextPanelRight.defaultX = this._npcTextPanelRight.x;
        this._npcTextPanelRight.defaultY = this._npcTextPanelRight.y;
        this._currentNpcTextPanel = this._npcTextPanelLeft;


        this._npcTextPanelLeft.visible = false;
        this._npcTextPanelRight.visible = false;

        // continue button handling
        this._handleContinueButtonClickedBound = this._handleContinueButtonClicked.bind(this);
        this.createButton(this._npcTextPanelLeft.continueButton, this._handleContinueButtonClickedBound);
        this.createButton(this._npcTextPanelRight.continueButton, this._handleContinueButtonClickedBound);

        this._handleSoundCompleteBound = this._handleSoundComplete.bind(this);
        this._handleSoundErrorBound = this._handleSoundError.bind(this);

        this._history = "";

        this.setDialogPlayer(this._skin["dialogPlayer"]);

        const dialogContent = this.getDialogPlayer().getContent();
        if (!isNull(dialogContent.getNpcID())) {
            this._npcContent = this._npcModel.getNpcByNpcID(dialogContent.getNpcID());
        }

        // Initialize NPC text
        this._updateTextDisplay(false);

        // Skipping content
        this._skipContentFunction = this._onSkipContent.bind(this);
        this._dispatcher.addEventListener(DebugEvent.SKIP_CONTENT, this._skipContentFunction);

        //Pause/resume
        this._handlePauseGameBound = this._handlePauseGame.bind(this);
        this._handleResumeGameBound = this._handleResumeGame.bind(this);
        this._dispatcher.addEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this._dispatcher.addEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);

        this._onImageLoadedBound = this._onImageLoaded.bind(this);

        this._pauseVideoPlayerButton = this._mediatorManager.createMediator(SkinFactory.getSkin(SkinKeyRegistry.PAUSE_VIDEO_PLAYER_BUTTON_SKIN), SkinKeyRegistry.PAUSE_VIDEO_PLAYER_BUTTON_SKIN);
        this._pauseVideoPlayerButton.setParent(this._skin);
    }

    destroy() {
        this._dialogPlayerOptionsMediator.destroy();
        this._dialogPlayerOptionsMediator = null;

        this._pauseVideoPlayerButton.destroy();

        //unbind continue buttons
        this._npcTextPanelLeft.continueButton.removeEventListener('click', this._handleContinueButtonClickedBound);
        this._npcTextPanelRight.continueButton.removeEventListener('click', this._handleContinueButtonClickedBound);

        this._dispatcher.removeEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this._dispatcher.removeEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);
        this._dispatcher.removeEventListener(DebugEvent.SKIP_CONTENT, this._skipContentFunction);

        this._dialogTimerMediator.destroy();
        this._dialogTimerMediator = null;

        for (const key in this._imagesContent) {
            this._imagesContent[key] = null;
        }
        this._imagesContent = null;

        // make sure that there are no timeouts or lazy loading callbacks on hold
        this._cancelImageLoadingTimeout();
        this._assetLoadService.cancelLazyLoadingCallbacksForGroup(DialogPlayerMediator.LAZY_LOADING_GROUP);

        this._skin = null;
        this._config = null;

        super.destroy();
    }

    // -----------------------------------------
    // Continue button
    // -----------------------------------------

    _handleContinueButtonClicked(e) {
        this._soundManager.playSound(SoundType.SOUND_CLICK);
        this.getDialogPlayer().optionChosen(this.continueOption);
    }

    // ------------------------------------------
    // 	Dialog Content handlers
    // ------------------------------------------
    _handleTextContent(content) {
        this._npcTextPanelLeft.visible = false;
        this._npcTextPanelRight.visible = false;

        // auto hide continue buttons
        this._npcTextPanelLeft.continueButton.visible = this._npcTextPanelRight.continueButton.visible = false;

        const balloonType = content.getBalloonType();

        // Balloon type: arrow to left or right
        if (balloonType === 'right') {
            this._currentNpcTextPanel = this._npcTextPanelRight;

        } else {
            // Assume left - default
            this._currentNpcTextPanel = this._npcTextPanelLeft;
        }

        this.npcTitleTextField = this._currentNpcTextPanel.npcNamePanel.textField;

        this._addToHistory(this.npcID, content.getText());

        // Custom X and Y coordinates
        const x = content.getX();
        const y = content.getY();
        if (x !== 0) {
            this._currentNpcTextPanel.x = x;
        } else {
            this._currentNpcTextPanel.x = this._currentNpcTextPanel.defaultX;
        }
        if (y !== 0) {
            this._currentNpcTextPanel.y = y;
        } else {
            this._currentNpcTextPanel.y = this._currentNpcTextPanel.defaultY;
        }

        super._handleTextContent(content);
    }

    _handleAudioContent(content) {
        this._currentAudioContent = content;
        const soundURL = this._assetLocator.getLocationForContent(content, this._scenarioLibrary.getCurrentScenario().id);
        this._currentAudioID = this._soundManager.streamSound(soundURL, this._handleSoundCompleteBound, this._handleSoundErrorBound);
        console.info(`Dialog handling url: ${soundURL} :id: ${this._currentAudioID}`);
        super._handleAudioContent(content);
    }

    _handleSoundComplete(id) {
        // Complete the audio content
        this._currentAudioID = null;
        this.getDialogPlayer().contentCompleted(this._currentAudioContent);
    }

    _handleSoundError(id) {
        // Complete the audio content, even though it was an error. This way the flow is not interrupted
        console.info(`[DialogPlayerMediatior] Error playing/streaming sound with id: ${this._currentAudioID}`);
        this._currentAudioID = null;
        this.getDialogPlayer().contentCompleted(this._currentAudioContent);
    }

    _handleImageContent(content) {
        const imageID = content.getImage();
        // cache the content for future purpose
        this._imagesContent[imageID] = content;
        // Timeout for when image cannot be loaded.
        // TODO -- this current solution does not properly support concurrent lazy loading images
        if (this.timeoutProxy == null) {
            this.timeoutProxy = {};
        }
        createjs.Tween.get(this.timeoutProxy).wait(DialogPlayerMediator.IMAGE_LAZYLOADING_TIMEOUT * 1000).call(this._onImageLoadingTimeout, [imageID], this);
        // get image URL
        const imageURL = this._assetLocator.getLocationForContent(content, this._scenarioLibrary.getCurrentScenario().id);
        // start lazy loading the image
        this._assetLoadService.lazyLoadImage(imageID, imageURL, this._onImageLoadedBound, DialogPlayerMediator.LAZY_LOADING_GROUP);
    }

    _onImageLoaded(imageID) {
        if (DialogPlayerMediator.USE_DOM_IMGS) {
            this._setDOMImage(imageID);
        }
        else {
            this._setSkinImage(imageID);
        }
    }

    _onImageLoadingTimeout(imageID) {
        console.log(`[DialogPlayerMediator] Lazy loading timeout for image: ${imageID} after ${DialogPlayerMediator.IMAGE_LAZYLOADING_TIMEOUT} seconds`);
        // cancel callback for this lazy loading image
        this._assetLoadService.cancelLazyLoadingCallbackForImage(imageID, DialogPlayerMediator.LAZY_LOADING_GROUP);
        // set image content as completed
        this._completeImageContent(imageID);
    }

    _cancelImageLoadingTimeout() {
        if (this.timeoutProxy != null) {
            createjs.Tween.removeTweens(this.timeoutProxy);
            this.timeoutProxy = null;
        }
    }

    _setSkinImage(imageID) {
        const imageType = this._imagesContent[imageID].getContentID();
        let imageContainer = null;

        // determine which container will parent the image
        switch (imageType) {
            // TODO - define here other image types
            default:
                if (isNull(this._skin.defaultImageContainer)) {
                    this._skin.defaultImageContainer = new createjs.Container();
                    this._skin.addChildAt(this._skin.defaultImageContainer, this._skin.getChildIndex(this._skin.bg) + 1); // create container on top of background
                    this.stretchBackground(this._skin.defaultImageContainer, new createjs.Rectangle(0, 0, 1920, 1080));
                }
                imageContainer = this._skin.defaultImageContainer;
        }

        // show image - by fading it in, or not
        let image = this._assetLibrary.getAsset(imageID);
        if (!(image instanceof createjs.Bitmap)) {
            image = new createjs.Bitmap(image);
        }
        var imageBounds = image.getBounds();

        image.regX = imageBounds.width * .5;
        image.regY = imageBounds.height * .5;

        // if there are other children in the image container, then the new image should fade in
        if (imageContainer.numChildren > 0) {
            // Kill all running tweens on the images so the onComplete doesn't get called more than 1 time (this removes all images)
            for (let i = 0, length = imageContainer.numChildren; i < length; i++) {
                createjs.Tween.removeTweens(imageContainer.getChildAt(i));
            }
            image.alpha = 0;
            imageContainer.addChildAt(image, imageContainer.numChildren);
            const onComplete = function () {
                // remove other children
                for (let i = 0, length = imageContainer.numChildren; i < length; i++) {
                    const child = imageContainer.getChildAt(i);
                    if (child !== image) {
                        imageContainer.removeChild(child);
                    }
                }
                // complete the image content
                this._completeImageContent(imageID);
            }.bind(this);
            createjs.Tween.get(image).to({ alpha: 1 }, 700).call(onComplete);
        }
        // otherwise, just show it and complete the image content
        else {
            imageContainer.addChild(image);
            this._completeImageContent(imageType);
        }
    }

    _setDOMImage(imageID) {
        const image = this._assetLibrary.getAsset(imageID).image;

        // Based on http://jsfiddle.net/HhpW5/5/
        console.log(` --- SETTING BG IMAGE TO ${image.src}  on div ${this._bgImgToggle}`);
        $(`#bgImg${this._bgImgToggle}`).css('background-image', `url(${image.src})`);
        $(`#bgImg${this._bgImgToggle}`).fadeIn(800, () => {
            this._bgImgToggle ^= 1;
            $(`#bgImg${this._bgImgToggle}`).fadeOut(800);
            // Img content is completed.
            this._completeImageContent();
        }); //, 'background-image', 'url(' + imageURL + ')');
    }

    _completeImageContent(imageID) {
        // make sure that there are no timeouts on hold
        this._cancelImageLoadingTimeout();

        // complete content
        const content = this._imagesContent[imageID];
        if (!isNull(content)) {
            super._handleImageContent(content);
            this._imagesContent[imageID] = null;
            delete this._imagesContent[imageID];
        }
    }

    _handleMovieContent(content) {
        // End any current video
        this.handleVideoEnded(null);

        this._content = content;
        const videoClass = VideoPlayerDispenserHelper.videoPlayerIds.mainVideoPlayer;
        const videoURL = this._assetLocator.getLocationForContent(this._content, this._scenarioLibrary.getCurrentScenario().id);
        const subtitlesURL = this._assetLocator.getSubtitleLocationForID(this._content, this._scenarioLibrary.getCurrentScenario().id);
        this.videoPlayer = new VideoPlayer(videoClass);
        this.videoPlayer.autoRetry = false; // Set auto retry to false as we want to show an error message that will trigger the retry
        this._pauseVideoPlayerButton.setVideoPlayer(this.videoPlayer);

        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_FINISHED, this.handleVideoEnded, this);
        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_READY, this.handleVideoReady, this);
        this.videoPlayer.addEventListener(VideoPlayerEvent.PLAYBACK_ERROR, this.handleVideoError, this);

        this.videoPlayer.load(videoURL, null, subtitlesURL, true);
    }

    handleVideoReady(evt) {
        this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_READY, this.handleVideoReady, this);
        this.videoPlayer.play();
        // If the video should not block the dialog player, complete the video immediately.
        if (!DialogPlayerMediator.VIDEO_IS_BLOCKING) {
            this._dialogPlayer.contentCompleted(this._content);
        }
    }

    handleVideoEnded(evt) {
        if (!isNull(this.videoPlayer)) {
            this.videoPlayer.stop();
            this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_READY, this.handleVideoReady, this);
            this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_FINISHED, this.handleVideoEnded, this);
            this.videoPlayer.removeEventListener(VideoPlayerEvent.PLAYBACK_ERROR, this.handleVideoError, this);
            this.videoPlayer.destroy();
            this.videoPlayer = null;
            this._pauseVideoPlayerButton.setVideoPlayer(null);
        }

        this._dialogPlayer.contentCompleted(this._content);
    }

    handleVideoError(evt) {
        const systemMessage = new SystemMessageBuilder()
            .setTitle(this._languagePack.getText('assetLoading.videoLoadError.title'))
            .setDescription(this._languagePack.getText('assetLoading.videoLoadError.description'))
            .addOption({
                text: this._languagePack.getText('assetLoading.videoLoadError.okButtonLabel'),
                callback: () => {
                    this.videoPlayer.reload();
                },
            })
            .build();
        this.showSystemMessage(systemMessage);
    }

    // ------------------------------------------
    // 	 Dialog displaying
    // ------------------------------------------
    _onDialogEnd(evt) {
        // End any current video
        this.handleVideoEnded(null);

        super._onDialogEnd(evt);
    }

    _handleDialogOptionsChanged() {
        // Check for "continue" option
        const currentOptions = this.getDialogPlayer().getCurrentOptions();

        for (let i = 0; i < currentOptions.length; i++) {

            if (DialogOptionHelper.isContinueOption(currentOptions[i])) {
                this._currentNpcTextPanel.continueButton.visible = true;
                this.continueOption = currentOptions[i];
            }
        }
    }

    _handleDialogOptionChosen(option) {
        const content = option.getData().getData().content;
        if (content !== null && DialogPlayerMediator.USE_CHAT_HISTORY) {
            this._addToHistory(this.youID, content.getText());
        }

        super._handleDialogOptionChosen(option);
    }

    _addToHistory(id, strHistory) {
        if (DialogPlayerMediator.USE_CHAT_HISTORY) {
            // TODO: make HTML text possible
            this._history += `${id}:\n${strHistory}\n\n`;
        } else {
            this._history = strHistory;
        }
        this._updateTextDisplay(true);
    }

    _updateTextDisplay(animate) {
        if (animate) {
            const animation = TextTypeWriterAnimation.animate(this._currentNpcTextPanel.npcTextPanel.npcTextField, this._history, 1, 0, null, null, () => {
                this._currentNpcTextPanel.slicedNpcTextPanel.update();
            });
        }
        else {
            this._currentNpcTextPanel.npcTextPanel.npcTextField.text = this._history;
            this._currentNpcTextPanel.slicedNpcTextPanel.update();
        }

        const hasText = !!this._history.trim();
        if (hasText) {
            this._currentNpcTextPanel.visible = true;
            createjs.Tween.get(this._currentNpcTextPanel, { override: true })
                .to({ alpha: 1 }, 150, createjs.Ease.sineInOut);
        } else {
            this._currentNpcTextPanel.npcTextPanel.npcTextField.text = "";
            this._currentNpcTextPanel.slicedNpcTextPanel.update();
            createjs.Tween.get(this._currentNpcTextPanel, { override: true })
                .to({ alpha: 0 }, 150, createjs.Ease.sineInOut);
        }

        // NPC name
        let found = false;
        // First see if explicit NPC defined for the current content       
        if (!isNull(this._currentCharacterID)) {
            const npcContent = this._npcModel.getNpcByNpcID(this._currentCharacterID);
            if (!isNull(npcContent)) {
                found = true;
                this._currentNpcTextPanel.npcNamePanel.textField.text = npcContent.getName();
            }
        }

        // Then see if NPC defined for the dialog as a whole
        if (!found) {
            if (!isNull(this._npcContent)) {
                this._currentNpcTextPanel.npcNamePanel.textField.text = this._npcContent.getName();
            } else {
                this._currentNpcTextPanel.npcNamePanel.textField.text = "";
            }
        }
    }

    // ------------------------------------------
    //			PAUSE / RESUME / SKIP
    // ------------------------------------------
    _onSkipContent(e) {
        if (this._config.getValue("settings.skippableContent")) {
            if (!isNull(this._currentAudioID)) {
                this._soundManager.stopStreamSound(this._currentAudioID);
                this._handleSoundComplete(this._currentAudioID);
            }

            if (!isNull(this._content) && this._content.getType() === MovieContent.TYPE) {
                this.handleVideoEnded(null);
            }
        }
    }

    _handlePauseGame(evt) {
        this.setPaused(true);
    }

    _handleResumeGame(evt) {
        this.setPaused(false);
    }

    setPaused(value) {
        console.log(`DialogPlayerMediator.setPaused: ${value}, video: ${!isNull(this.videoPlayer)} audio: ${!isNull(this._currentAudioID)}`);
        if (this.videoPlayer) {
            if (value) {
                this.videoPlayer.pause();
            } else {
                this.videoPlayer.resume();
            }
        }
        if (!isNull(this._currentAudioID)) {
            if (value)
                this._soundManager.pause();
            else
                this._soundManager.resume();
        }
    }
}

DialogPlayerMediator.USE_CHAT_HISTORY = false;
DialogPlayerMediator.USE_DOM_IMGS = false;
DialogPlayerMediator.IMAGE_LAZYLOADING_TIMEOUT = 3; // in seconds
DialogPlayerMediator.VIDEO_IS_BLOCKING = false;
DialogPlayerMediator.LAZY_LOADING_GROUP = 'DialogPlayerMediator';